import { syncState } from "@legendapp/state";
import { useObserve, useValue } from "@legendapp/state/react";
import { Spinner } from "@repo/ui/ui/spinner";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { datasources$ } from "~/stores/datasources";

/**
 * Resume fallback for `/onboard/catalog`. Hit when the user lands on the
 * bare path (e.g. after a refresh during the catalog step, or via the
 * `/onboard/index` resume switch). Reactively waits for `datasources$` to
 * hydrate, then forwards to the most recently created datasource's catalog
 * page. If the org has no datasources at all, advances to the celebration
 * step so the wizard isn't dead-ended.
 */
const ResumeCatalog = () => {
  const navigate = useNavigate();
  const isLoaded = useValue(syncState(datasources$).isLoaded);

  useObserve(() => {
    if (!syncState(datasources$).isLoaded.get()) return;

    const items = datasources$.get();
    const list = items ? Object.values(items) : [];
    const latest = [...list].sort((a, b) => {
      const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    })[0];

    queueMicrotask(() => {
      if (latest) {
        navigate({
          to: "/onboard/catalog/$datasourceId",
          params: { datasourceId: latest.id },
          replace: true,
        });
      } else {
        navigate({ to: "/onboard/done", replace: true });
      }
    });
  });

  return (
    <div className="flex min-h-96 flex-1 items-center justify-center">
      <Spinner className="size-6" />
      {!isLoaded && <span className="sr-only">Loading datasources</span>}
    </div>
  );
};

export const Route = createFileRoute("/onboard/catalog/")({
  component: ResumeCatalog,
});
